/**
 * Tipagens do módulo Semog Locações — pipeline comercial, aprovações e BI.
 * Alinhadas ao PDF de requisitos e ao backend (locacoesStore / locacoesPipeline).
 */

/** Fases do funil Kanban de contratos */
export type ContratoPipelineFase =
  | "captacao"
  | "setup"
  | "vigente"
  | "proximo_vencimento"
  | "encerrado";

export const CONTRATO_PIPELINE_FASE_LABELS: Record<ContratoPipelineFase, string> = {
  captacao: "Captação / Negociação",
  setup: "Setup / Aguardando início",
  vigente: "Vigente",
  proximo_vencimento: "Próximo ao vencimento",
  encerrado: "Encerrado / Distratado",
};

export const CONTRATO_PIPELINE_FASE_COLORS: Record<ContratoPipelineFase, string> = {
  captacao: "border-sky-200 bg-sky-50",
  setup: "border-amber-200 bg-amber-50",
  vigente: "border-emerald-200 bg-emerald-50",
  proximo_vencimento: "border-orange-200 bg-orange-50",
  encerrado: "border-slate-200 bg-slate-100",
};

/** Status de aprovação do gestor (horímetro, odômetro, manutenção) */
export type AprovacaoStatus = "pendente" | "aprovado" | "rejeitado";

export const APROVACAO_STATUS_LABELS: Record<AprovacaoStatus, string> = {
  pendente: "Pendente",
  aprovado: "Aprovado",
  rejeitado: "Rejeitado",
};

export type FilaAprovacaoTipo = "horimetro" | "manutencao";

/** Item na fila de aprovação (widget da Visão Geral) */
export interface FilaAprovacaoItem {
  id: string;
  tipo: FilaAprovacaoTipo;
  titulo: string;
  subtitulo: string;
  maquinaId: string;
  maquinaNome: string;
  contratoId?: string;
  contratoLabel?: string;
  solicitanteNome: string;
  dataHora: string;
  statusAprovacao: AprovacaoStatus;
  motivoRejeicao?: string;
  detalhe?: string;
}

/** Apontamento de horímetro/odômetro (log do operador) */
export interface LogEntry {
  id: string;
  maquinaId: string;
  maquinaNome: string;
  contratoId?: string;
  tipo: "horimetro" | "odometro";
  horimetroInformado: number;
  odometroInformado?: number;
  observacao: string;
  usuarioId: string;
  usuarioNome: string;
  dataHora: string;
  statusAprovacao: AprovacaoStatus;
  motivoRejeicao?: string;
  origem: "gestor" | "operador";
}

/** Operador vinculado a contrato / máquina */
export interface Operator {
  id: string;
  nome: string;
  documento: string;
  telefone: string;
  observacao: string;
  contratoIds?: string[];
  maquinaIds?: string[];
}

/** Máquina com metas de eficiência */
export interface Machine {
  id: string;
  nome: string;
  codigoInterno: string;
  tipo: string;
  status: string;
  horimetroAtual: number;
  odometroAtual: number;
  /** Limite L/h para alerta de alto consumo */
  consumoLimiteLph: number;
}

/** Contrato enriquecido para o Kanban */
export interface ContractPipelineCard {
  id: string;
  pipelineFase: ContratoPipelineFase;
  modeloContrato: string;
  clienteSiengeNome: string;
  clienteSiengeDocument: string;
  infoBasicos?: string;
  valorContrato: number;
  valorPotencial?: number;
  valorPeriodo?: number;
  horasPeriodo?: number;
  porHoras?: boolean;
  maquinaIds: string[];
  maquinaNomes: string[];
  operadoresCount: number;
  dataInicio: string;
  dataFimPrevista: string | null;
  ultimoFollowUp?: string;
  motivoEncerramento?: string;
  status: string;
  /** Saúde operacional: apontamentos em dia */
  saudeApontamentos: "ok" | "atrasado" | "sem_dados";
  pendencias: string[];
}

/** Resumo financeiro / BI estratégico */
export interface LocacoesBiSummary {
  faturamentoTotal: number;
  gastosTotal: number;
  gastosCombustivel: number;
  gastosManutencao: number;
  margem: number;
  margemPercentual: number;
  rentabilidadePorContrato: Array<{
    contratoId: string;
    clienteNome: string;
    faturamento: number;
    gastos: number;
    margem: number;
  }>;
  eficienciaMaquinas: Array<{
    maquinaId: string;
    maquinaNome: string;
    maquinaTipo: string;
    litrosPorHora: number;
    limiteLph: number;
    alertaAltoConsumo: boolean;
  }>;
}
